import type { CourseCardCurrency, CourseCardItem, CourseCardModality } from '../types/course-card';

const CURRENCY_LOCALES: Record<CourseCardCurrency, string> = {
  ARS: 'es-AR',
  CLP: 'es-CL',
  COP: 'es-CO',
  MXN: 'es-MX',
};

const CURRENCY_TIMEZONES: Record<CourseCardCurrency, string> = {
  ARS: 'America/Argentina/Buenos_Aires',
  CLP: 'America/Santiago',
  COP: 'America/Bogota',
  MXN: 'America/Mexico_City',
};

const toCalendarKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

const todayCalendarKey = (today: Date, currency?: CourseCardCurrency): string => {
  const timeZone = currency ? CURRENCY_TIMEZONES[currency] : undefined;
  if (!timeZone) return toCalendarKey(today);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(today);
};

export const formatCourseCardCurrency = (value: number, currency: CourseCardCurrency): string => {
  const amount = new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
    maximumFractionDigits: 0,
  }).format(value);

  return `${currency} ${amount}`;
};

export const formatCourseCardCompactPrice = (
  value: number,
  currency: CourseCardCurrency,
): string => {
  if (Math.abs(value) >= 1000000) {
    const amount = new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(value / 1000000);

    return `${currency} ${amount}M`;
  }

  return `${currency} ${Math.round(value / 1000)}k`;
};

export const getCourseCardPriceParts = (
  value: number,
  currency: CourseCardCurrency,
): { currencyLabel: CourseCardCurrency; amountLabel: string } => {
  const formattedPrice = formatCourseCardCurrency(value, currency);
  const amountLabel = formattedPrice.replace(`${currency} `, '');

  return { currencyLabel: currency, amountLabel };
};

const COURSE_CARD_MODALITY_LABELS: Record<CourseCardItem['modality'], string> = {
  'en-vivo': 'En vivo',
  asincronica: 'Asincrónica',
  presencial: 'Presencial',
  mixta: 'Mixta',
};

export const formatCourseCardModality = (modality: CourseCardItem['modality']): string =>
  COURSE_CARD_MODALITY_LABELS[modality];

export const formatCourseCardRating = (rating: number): string => rating.toFixed(1);

export const formatCourseCardCount = (value: number): string =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(value);

export const formatCourseCardPrimaryCategory = (primaryCategory?: string): string | null => {
  if (!primaryCategory) return null;

  // Una categoría puede traer varios elementos (ej. "Diplomado, Curso"). Los
  // separadores multivalor explícitos son coma, barra y slash; NO el espacio,
  // para no romper categorías multipalabra como "Salud Mental".
  const segments = primaryCategory
    .split(/[,/|•]+/)
    .map(segment => segment.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().toLocaleLowerCase('es-CL'))
    .filter(Boolean)
    .map(segment => `${segment.charAt(0).toLocaleUpperCase('es-CL')}${segment.slice(1)}`);

  if (segments.length === 0) return null;

  return segments.join(' • ');
};

export const getCourseCardTeachers = (
  course: Pick<CourseCardItem, 'instructorName' | 'instructorRole' | 'teachers'>,
): NonNullable<CourseCardItem['teachers']> => {
  if (course.teachers?.length) return course.teachers;
  if (!course.instructorName) return [];

  return [{ name: course.instructorName, excerpt: course.instructorRole }];
};

export const getCourseCardTeacherInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase('es-CL'))
    .join('');

export const formatCourseCardTeachersSummary = (
  course: Pick<CourseCardItem, 'instructorName' | 'instructorRole' | 'teachers'>,
): string | null => {
  const teachers = getCourseCardTeachers(course);
  const firstTeacher = teachers[0];

  if (!firstTeacher) return null;

  const extraTeachersCount = teachers.length - 1;
  if (extraTeachersCount <= 0) return firstTeacher.name;

  const suffix = extraTeachersCount === 1 ? 'docente' : 'docentes';
  return `${firstTeacher.name} +${extraTeachersCount} ${suffix}`;
};

interface ParsedStartDate {
  date: Date;
  hasTime: boolean;
}

const formatTimePeriod = (timeLabel: string): string =>
  timeLabel.replace(/\s*a\.\s*m\./i, ' am').replace(/\s*p\.\s*m\./i, ' pm');

const formatDateTimeParts = ({ date, hasTime }: ParsedStartDate): string => {
  const dateLabel = new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);

  if (!hasTime) return dateLabel;

  const timeLabel = new Intl.DateTimeFormat('es-CL', {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
  })
    .format(date)
    .replace(/\s/g, ' ')
    .toLowerCase();

  return `${dateLabel} | ${formatTimePeriod(timeLabel)}`;
};

const parseStartDate = (startDate: string): ParsedStartDate | null => {
  const isoDateTimeMatch = startDate.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::\d{2})?)?$/,
  );

  if (isoDateTimeMatch) {
    const [, year, month, day, hour = '0', minute = '0'] = isoDateTimeMatch;

    return {
      date: new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)),
      hasTime: Boolean(isoDateTimeMatch[4]),
    };
  }

  const slashDateMatch = startDate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (slashDateMatch) {
    const [, day, month, year] = slashDateMatch;

    return {
      date: new Date(Number(year), Number(month) - 1, Number(day)),
      hasTime: false,
    };
  }

  return null;
};

export const formatCourseCardStartDate = (
  startDate?: string,
  modality?: CourseCardModality,
  currency?: CourseCardCurrency,
  today?: Date | null,
): string | null => {
  if (!startDate) {
    return modality === 'asincronica' ? '¡Inicia hoy!' : null;
  }

  const parsedDate = parseStartDate(startDate);

  if (!parsedDate) return `Inicia ${startDate}`;

  const verb =
    today && toCalendarKey(parsedDate.date) < todayCalendarKey(today, currency) ? 'Inició' : 'Inicia';

  return `${verb} ${formatDateTimeParts(parsedDate)}`;
};
